import React, {useEffect, useMemo, useState} from "react";
import {Button, DialogContent, DialogTitle, Modal, ModalClose, ModalDialog, Stack, Typography, CircularProgress} from "@mui/joy";
import {Elements, PaymentElement, useElements, useStripe} from "@stripe/react-stripe-js";
import {toast} from "react-toastify";
import GeneralApiService from "../../axios/Axios";
import {stripePromise} from "../../stripeConfig";

interface Props {
    open: boolean;
    onClose: () => void;
    feeId: number | null;
    onPaid: () => void;
}

function SetupForm({clientSecret, onPaymentMethod}: { clientSecret: string, onPaymentMethod: (pm: string) => void }) {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!stripe || !elements) return;
        setSubmitting(true);
        const result = await stripe.confirmSetup({
            elements,
            redirect: "if_required",
        });
        if (result.error) {
            toast.error(result.error.message || "Failed to add card.");
            setSubmitting(false);
            return;
        }
        if (result.setupIntent?.status === "succeeded" && result.setupIntent.payment_method) {
            onPaymentMethod(result.setupIntent.payment_method as string);
        }
        setSubmitting(false);
    };

    return (
        <>
            <PaymentElement options={{layout: "tabs"}}/>
            <Button fullWidth onClick={handleSubmit} loading={submitting} sx={{mt: 2}}>Pay</Button>
        </>
    );
}

export default function FranchiseFeePaymentModal({open, onClose, feeId, onPaid}: Props) {
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            setClientSecret(null);
            const startSetup = async () => {
                setLoading(true);
                try {
                    const res = await GeneralApiService.createSetupIntent();
                    if (res?.client_secret) {
                        setClientSecret(res.client_secret);
                    } else {
                        toast.error("Could not start payment.");
                    }
                } catch (e) {
                    toast.error("Could not start payment.");
                } finally {
                    setLoading(false);
                }
            };
            startSetup();
        }
    }, [open]);

    const handlePaymentMethod = async (pmId: string) => {
        if (!feeId) return;
        try {
            await GeneralApiService.payFranchiseFee(feeId, pmId);
            toast.success("Payment attempt started/processed.");
            await onPaid();
            onClose();
        } catch (e) {
            toast.error("Payment failed. Check card on file or try again.");
        }
    };

    const options = useMemo(() => clientSecret ? {
        clientSecret,
        appearance: {theme: 'stripe' as const}
    } : null, [clientSecret]);

    return (
        <Modal open={open} onClose={onClose} keepMounted>
            <ModalDialog
                sx={{
                    maxHeight: '80vh',
                    overflowY: 'auto',
                    minWidth: { xs: 'auto', sm: 420 },
                    width: { xs: '95vw', sm: 'auto' },
                    maxWidth: { xs: '95vw', sm: '450px' },
                }}
            >
                <ModalClose />
                <DialogTitle>Pay Franchise Fee</DialogTitle>
                <DialogContent>
                    <Stack spacing={1.5}>
                        <Typography level="body-sm" color="neutral">
                            Select a saved card or add a new one, then pay the franchise fee.
                        </Typography>
                        {loading || !options ? (
                            <CircularProgress />
                        ) : (
                            <Elements stripe={stripePromise} options={options}>
                                <SetupForm clientSecret={clientSecret!} onPaymentMethod={handlePaymentMethod} />
                            </Elements>
                        )}
                    </Stack>
                </DialogContent>
            </ModalDialog>
        </Modal>
    );
}

